import { useEffect, useMemo } from 'react';
import { useTenantEntitlements } from '@/hooks/useTenantEntitlements';
import { useAuthStore } from '@/stores/useAuthStore';
import { UserRole } from '@/types/user';

export type TrialPhase =
  /** Not on a trial and not on post-trial free — nothing to say. */
  | 'none'
  /** Trial running, plenty of time. Informational only. */
  | 'active'
  /** Trial running, inside the last week. Nudge, don't shout. */
  | 'ending'
  /** Trial over, tenant dropped to FREE — AI is off until they subscribe. */
  | 'lapsed';

export interface TrialStatus {
  phase: TrialPhase;
  /** Whole days remaining, 0 on the final day. Null when not trialing. */
  daysLeft: number | null;
  /** Owners are the only role that can actually buy — everyone else
   *  gets no CTA, because prompting them is a dead end. */
  canSubscribe: boolean;
  /** Convenience: should ANY conversion surface render at all. */
  show: boolean;
}

/** Inside this many days the trial CTA escalates from info to nudge. */
const ENDING_SOON_DAYS = 7;

/** The three fields the trial surfaces actually need, cached per tenant. */
interface TrialSnapshot {
  trialActive: boolean;
  trialEndsAt: string | null;
  isFreeTier: boolean;
}

/** Per-tenant: a stale snapshot from another clinic is worse than none. */
const cacheKey = (tenantId: string) => `maie.trial-status.${tenantId}`;

const readSnapshot = (tenantId: string | null): TrialSnapshot | null => {
  if (!tenantId) return null;
  try {
    const raw = localStorage.getItem(cacheKey(tenantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TrialSnapshot>;
    if (typeof parsed?.isFreeTier !== 'boolean') return null;
    return {
      trialActive: Boolean(parsed.trialActive),
      trialEndsAt:
        typeof parsed.trialEndsAt === 'string' ? parsed.trialEndsAt : null,
      isFreeTier: parsed.isFreeTier,
    };
  } catch {
    return null;
  }
};

const writeSnapshot = (tenantId: string | null, snap: TrialSnapshot): void => {
  if (!tenantId) return;
  try {
    localStorage.setItem(cacheKey(tenantId), JSON.stringify(snap));
  } catch {
    // Private mode / quota — the surfaces just fall back to fetch-then-show.
  }
};

/**
 * Single source of truth for "is this clinic on a trial, and how urgent
 * is it".
 *
 * The backend has exposed `trialActive` / `trialEndsAt` all along, but the
 * only consumer was the Subscription page itself — a page nested under
 * Administration that a trialing clinic has no reason to visit. So a
 * clinic could run the entire 30-day trial, lose AI at the end, and never
 * once have been told they were on a trial or shown a way to subscribe.
 *
 * Resolution order, and why:
 *   1. Live entitlements once they land.
 *   2. Last known snapshot for this tenant, so a paying clinic renders the
 *      right thing (usually nothing) on the very first frame.
 *   3. `none` while genuinely unknown. Never the free-tier default — that
 *      read as "trial ended" and flashed a red card on every home-page
 *      load until the entitlements fetch resolved.
 */
export function useTrialStatus(): TrialStatus {
  const { raw } = useTenantEntitlements();
  const user = useAuthStore((s) => s.user);
  const tenantId = useAuthStore((s) => s.activeTenantId);
  const canSubscribe = user?.primaryRole === UserRole.OWNER;

  const liveTrialActive = raw?.trialActive;
  const liveTrialEndsAt = raw?.trialEndsAt ?? null;
  const liveIsFreeTier = raw?.isFreeTier;

  // Keep the cache warm for the next load. Only ever written from live data.
  useEffect(() => {
    if (liveIsFreeTier === undefined || liveTrialActive === undefined) return;
    writeSnapshot(tenantId, {
      trialActive: liveTrialActive,
      trialEndsAt: liveTrialEndsAt,
      isFreeTier: liveIsFreeTier,
    });
  }, [tenantId, liveTrialActive, liveTrialEndsAt, liveIsFreeTier]);

  return useMemo<TrialStatus>(() => {
    const live =
      liveIsFreeTier !== undefined && liveTrialActive !== undefined
        ? {
            trialActive: liveTrialActive,
            trialEndsAt: liveTrialEndsAt,
            isFreeTier: liveIsFreeTier,
          }
        : null;
    const snap = live ?? readSnapshot(tenantId);

    const idle: TrialStatus = {
      phase: 'none',
      daysLeft: null,
      canSubscribe,
      show: false,
    };
    if (!snap) return idle;

    const daysLeft =
      snap.trialActive && snap.trialEndsAt
        ? Math.max(
            0,
            Math.ceil(
              (new Date(snap.trialEndsAt).getTime() - Date.now()) / 86_400_000,
            ),
          )
        : null;

    // A cached window that has since run out tells us nothing: the clinic
    // either subscribed or lapsed, and guessing "lapsed" is the wrong guess
    // to make. Stay quiet until the live snapshot says which.
    if (!live && snap.trialActive && daysLeft === 0) return idle;

    let phase: TrialPhase = 'none';
    if (snap.trialActive) {
      phase =
        daysLeft !== null && daysLeft <= ENDING_SOON_DAYS ? 'ending' : 'active';
    } else if (snap.isFreeTier) {
      // Post-trial FREE: the manual EMR still works, AI is switched off.
      // This is the highest-intent moment we have, and it had no surface.
      phase = 'lapsed';
    }

    return {
      phase,
      daysLeft,
      canSubscribe,
      show: phase !== 'none',
    };
  }, [
    tenantId,
    liveTrialActive,
    liveTrialEndsAt,
    liveIsFreeTier,
    canSubscribe,
  ]);
}
